import React, { useState } from 'react';
import { IoLockClosedOutline, IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import toast, { Toaster } from 'react-hot-toast';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

export default function ChangePasswordContent() {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        old: false,
        new: false,
        confirm: false
    });
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const validateForm = () => {
        if (!formData.oldPassword) {
            toast.error(t('changePasswordContent.oldPasswordRequired'));
            return false;
        }
        if (!formData.newPassword) {
            toast.error(t('changePasswordContent.newPasswordRequired'));
            return false;
        }
        if (formData.newPassword.length < 6) {
            toast.error(t('changePasswordContent.passwordMinLength'));
            return false;
        }
        if (formData.newPassword !== formData.confirmPassword) {
            toast.error(t('changePasswordContent.passwordsNotMatch'));
            return false;
        }
        if (formData.oldPassword === formData.newPassword) {
            toast.error(t('changePasswordContent.samePasswordError'));
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            const response = await api.changePassword(formData.oldPassword, formData.newPassword);

            // API response format kontrolü - hem Success hem success kontrolü
            // Response formatları: {"Success":true,"Message":"..."} veya {"success":true,"message":"..."}
            const isSuccess = response?.Success === true || response?.success === true;

            if (isSuccess) {
                const successMessage = response?.Message || response?.message || t('changePasswordContent.passwordChangedSuccess');
                toast.success(successMessage);
                // Form sıfırla
                setFormData({
                    oldPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                // Password visibility'yi de sıfırla
                setShowPasswords({
                    old: false,
                    new: false,
                    confirm: false
                });
            } else {
                // Başarısız response ama exception fırlatılmadı
                const errorMessage = response?.Message || response?.message || t('changePasswordContent.passwordChangeError');
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error('Şifre değiştirme hatası:', error);
            
            // Axios error response formatı: error.response?.data
            // Veya direkt error object formatı
            let errorMessage = t('changePasswordContent.passwordChangeFailed');
            
            if (error?.response?.data) {
                // Axios error response
                const errorData = error.response.data;
                errorMessage = errorData?.Message || errorData?.message || errorData?.error || errorMessage;
            } else if (error?.Message) {
                // API error response formatı
                errorMessage = error.Message;
            } else if (error?.message) {
                // Standard error message
                errorMessage = error.message;
            }
            
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="account-content">
            <div className="change-password-content">
                <div className="change-password-header">
                    <h1>{t('changePasswordContent.title')}</h1>
                    <p>{t('changePasswordContent.subtitle')}</p>
                </div>

                <form onSubmit={handleSubmit} className="change-password-form">
                    {/* Eski Şifre */}
                    <div className="form-group">
                        <label htmlFor="oldPassword" className="form-label">
                            <IoLockClosedOutline size={16} className='me-2' />
                            {t('changePasswordContent.oldPasswordLabel')}
                        </label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPasswords.old ? 'text' : 'password'}
                                id="oldPassword"
                                name="oldPassword"
                                value={formData.oldPassword}
                                onChange={handleInputChange}
                                className="form-control"
                                placeholder={t('changePasswordContent.oldPasswordPlaceholder')}
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => togglePasswordVisibility('old')}
                            >
                                {showPasswords.old ? <IoEyeOffOutline size={16} /> : <IoEyeOutline size={16} />}
                            </button>
                        </div>
                    </div>

                    {/* Yeni Şifre */}
                    <div className="form-group">
                        <label htmlFor="newPassword" className="form-label">
                            <IoLockClosedOutline size={16} className='me-2' />
                            {t('changePasswordContent.newPasswordLabel')}
                        </label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPasswords.new ? 'text' : 'password'}
                                id="newPassword"
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleInputChange}
                                className="form-control"
                                placeholder={t('changePasswordContent.newPasswordPlaceholder')}
                                required
                                minLength="6"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => togglePasswordVisibility('new')}
                            >
                                {showPasswords.new ? <IoEyeOffOutline size={16} /> : <IoEyeOutline size={16} />}
                            </button>
                        </div>
                    </div>

                    {/* Yeni Şifre Onay */}
                    <div className="form-group">
                        <label htmlFor="confirmPassword" className="form-label">
                            <IoLockClosedOutline size={16} className='me-2' />
                            {t('changePasswordContent.confirmPasswordLabel')}
                        </label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPasswords.confirm ? 'text' : 'password'}
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                className="form-control"
                                placeholder={t('changePasswordContent.confirmPasswordPlaceholder')}
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => togglePasswordVisibility('confirm')}
                            >
                                {showPasswords.confirm ? <IoEyeOffOutline size={16} /> : <IoEyeOutline size={16} />}
                            </button>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="form-actions">
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={loading}
                        >
                            {loading ? t('changePasswordContent.changing') : t('changePasswordContent.submitButton')}
                        </button>
                    </div>
                </form>
            </div>
            <Toaster
                position="top-center"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                    },
                    success: {
                        duration: 3000,
                        style: {
                            background: '#10B981',
                            color: '#fff',
                        },
                    },
                    error: {
                        duration: 5000,
                        style: {
                            background: '#EF4444',
                            color: '#fff',
                        },
                    },
                }}
            />
        </div>
    );
}
